// 先代の姫の「指折りを灯りの回数だけで知らせる」影絵の模型（2026-11-05 夕・台帳 docs/ledger/bosses.md の先代の行）。
//   作者（11-05）「ライトの予告を全体で2秒と固定して、1回なら2秒間で1回2秒の真ん中あたりで光らせるし、5回なら2秒の中で5回等間隔」
//   「点滅じゃなくてその方法で僕が言ってることを表現したい」「瞬間ってのが反応できるレベルならOK」
//   形：予告の窓（W ms・仮に 2秒）の中で、追う照明の輪が N 段に分けて等間隔に狭まる（k 段目は窓の (k−0.5)/N の所）。
//   最後の段で輪は主人公の位置に止まって白く強まる。窓が終わってから「間」（gap ms）を置いて技（仮の踏み込み突き）が出る。
//   先代は指を折らない。輪が主人公へ移るのは窓の前（TRAVEL ms）にして、窓の中の動きを狭まりだけにした（移る動きを1段目と数え違えないため）。
//   主人公は間の長さを見るため、わざと輪に残って受ける。
//   「えっと……」の4択（亡霊・鶏・軍配者たち・剣舞）は輪で数えず、技の始めの構えで見分ける。その構え4つを並べた模型も置く（構えの形は仮）。

import { circ, poly } from '../body'
import { bakeTracks, type MockupMove, type MockupPhase, type Pose, type StageEnv, type TrackKey, type Tracks, type Vec } from '../core'
import { STAND, build, step } from './princess_count_tells'

type Ctx = CanvasRenderingContext2D
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const ramp = (t: number, a: number, b: number) => clamp01((t - a) / (b - a))
const smooth = (u: number) => u * u * (3 - 2 * u)

/** 1回分の予告：n＝輪の段の数（借りるボスの番号）・win＝予告の窓・gap＝窓の終わりから技まで（ms） */
export interface RingSeg { n: number; win: number; gap: number }
/** 構え（輪は先代）→輪が主人公へ移る→窓→間→技→戻り の長さ（ms・仮） */
export const RING_BEAT = { rest: 600, travel: 300, after: 1100 }
const RB = RING_BEAT
export const segLen = (s: RingSeg) => RB.rest + RB.travel + s.win + s.gap + RB.after

interface SegAt { seg: RingSeg; o: number; ws: number; we: number; L: number; u: number; i: number }
function segTimes(segs: RingSeg[]): { seg: RingSeg; o: number; ws: number; we: number; L: number }[] {
  let o = 0
  return segs.map(seg => {
    const ws = o + RB.rest + RB.travel, we = ws + seg.win, L = we + seg.gap, r = { seg, o, ws, we, L }
    o += segLen(seg)
    return r
  })
}
/** k 段目（1 始まり）の時刻 */
export const stepTimes = (ws: number, s: RingSeg): number[] => Array.from({ length: s.n }, (_, k) => ws + (k + 0.5) / s.n * s.win)

// ---------- 主人公 ----------
const HERO0 = 157, WALK = 18
type HeroPose = 'stand' | 'hit'
function heroAt(T: ReturnType<typeof segTimes>, t: number): { x: number; pose: HeroPose; lean: number } {
  const s = findSeg(T, t)
  if (!s) return { x: HERO0, pose: 'stand', lean: 0 }
  // 窓の前半で少し下がる（輪が遅れて追うのが見える）→技を受けて飛ばされる→戻りで元の位置へ歩いて戻る
  const walk = smooth(ramp(t, s.ws - RB.travel, s.ws + s.seg.win * 0.4))
  const k1 = t >= s.L ? smooth(ramp(t, s.L, s.L + 140)) : 0
  const knock = t >= s.L ? 1 - smooth(ramp(t, s.L + 450, s.L + 1000)) : 1
  const back = smooth(ramp(t, s.L + 300, s.L + RB.after - 40))
  const x = HERO0 - WALK * walk * (1 - back) - 30 * Math.min(k1, knock)
  if (t >= s.L && t < s.L + 450) return { x, pose: 'hit', lean: -24 * (1 - ramp(t, s.L + 250, s.L + 450)) }
  return { x, pose: 'stand', lean: 0 }
}
function findSeg(T: ReturnType<typeof segTimes>, t: number): SegAt | null {
  for (let i = 0; i < T.length; i++) {
    const r = T[i]!, end = r.o + segLen(r.seg)
    if (t >= r.o && (t < end || i === T.length - 1)) return { ...r, u: t - r.o, i }
  }
  return null
}
function drawHeroFig(ctx: Ctx, h: { x: number; pose: HeroPose; lean: number }, e: StageEnv): void {
  const { k, G } = e, s = 0.62 * k, x = h.x * k, a = h.lean * Math.PI / 180
  const up = (dy: number): Vec => [x + dy * Math.sin(a), G - 52 * s - dy * Math.cos(a)]
  ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.8 * k; ctx.lineCap = 'round'; ctx.globalAlpha = 0.9
  const seg = (p: Vec, q: Vec) => { ctx.beginPath(); ctx.moveTo(p[0], p[1]); ctx.lineTo(q[0], q[1]); ctx.stroke() }
  const hip: Vec = [x, G - 52 * s], head = up(56 * s), sh = up(36 * s)
  ctx.beginPath(); ctx.arc(head[0], head[1], 12.6 * s, 0, Math.PI * 2); ctx.stroke()
  seg(hip, up(44 * s)); seg(hip, [x - 6 * k, G]); seg(hip, [x + 6 * k, G])
  const [[ax, ay], sw] = h.pose === 'hit' ? [[6, 30], -120] : [[12, 22], -40]
  const hand: Vec = [sh[0] + ax * s, sh[1] + (ay - 36) * s * 0.6]
  seg(sh, hand); seg(sh, [sh[0] - 8 * s, sh[1] + 22 * s])
  const sa = sw * Math.PI / 180
  ctx.strokeStyle = '#d8dde6'; ctx.lineWidth = 1.4 * k
  seg(hand, [hand[0] + Math.cos(sa) * 34 * s, hand[1] + Math.sin(sa) * 34 * s])
  ctx.globalAlpha = 1
}

// ---------- 追う照明（N 段に狭まる輪）----------
const HER_X = 300, W0 = 62, LOCK_W = 28, SNAP = 90
/** 輪：中心の x・半幅・白さ・明るさ */
export function ringAt(segs: RingSeg[], t: number): { x: number; w: number; white: number; a: number; steps: number } {
  const T = segTimes(segs), s = findSeg(T, t)
  if (!s) return { x: HER_X, w: W0, white: 0, a: 1, steps: 0 }
  const st = stepTimes(s.ws, s.seg), lockAt = st[st.length - 1]!
  const go = smooth(ramp(t, s.ws - RB.travel, s.ws)), ret = smooth(ramp(t, s.L + 600, s.L + 1000))
  // 何段狭まったか（段ごとに SNAP ms で詰まる）
  let done = 0, pulse = 0
  for (const at of st) {
    done += smooth(ramp(t, at, at + SNAP))
    if (t >= at) pulse = Math.max(pulse, 1 - ramp(t, at, at + 260))
  }
  const steps = st.filter(at => t >= at).length
  // 追う：主人公の 140ms 前の位置へ寄る。止まった後は止まった時の位置のまま
  const target = heroAt(T, Math.max(s.o, Math.min(t, lockAt) - 140)).x
  const x = HER_X + (target - HER_X) * go * (1 - ret)
  let w = W0 - (W0 - LOCK_W) * done / s.seg.n
  w = w + (W0 - w) * ret
  const white = smooth(ramp(t, lockAt, lockAt + SNAP)) * (1 - ret)
  return { x, w, white, a: (1 + 0.45 * pulse) * (1 + 0.5 * white), steps }
}
function drawRing(segs: RingSeg[]) {
  return (ctx: Ctx, t: number, e: StageEnv): void => {
    const { k, G } = e, sp = ringAt(segs, t), x = sp.x * k, w = sp.w * k, sx = 352 * k
    for (const [col, a] of [['#ffe6b0', 1 - sp.white], ['#ffffff', sp.white]] as const) {
      if (a <= 0.01) continue
      ctx.globalAlpha = Math.min(1, 0.09 * a * sp.a); poly(ctx, [[sx - 5 * k, -4 * k], [sx + 5 * k, -4 * k], [x + w, G], [x - w, G]], col)
      for (const [rr, aa] of [[1, 0.22], [0.62, 0.22]] as const) {
        ctx.globalAlpha = Math.min(1, aa * a * sp.a); ctx.fillStyle = col
        ctx.beginPath(); ctx.ellipse(x, G, w * rr, 7 * k * rr, 0, 0, Math.PI * 2); ctx.fill()
      }
      // 輪の縁（狭まりが読める線）
      ctx.globalAlpha = Math.min(1, 0.55 * a * sp.a); ctx.strokeStyle = col; ctx.lineWidth = 1.2 * k
      ctx.beginPath(); ctx.ellipse(x, G, w, 7 * k, 0, 0, Math.PI * 2); ctx.stroke()
    }
    ctx.globalAlpha = 1
  }
}

// ---------- 字幕と窓の帯 ----------
function drawCaption(segs: RingSeg[]) {
  const T = segTimes(segs)
  return (ctx: Ctx, t: number, e: StageEnv): void => {
    const { k, G, w } = e, s = findSeg(T, t)
    if (!s) return
    const font = (px: number) => `${px * k}px "Zen Kaku Gothic New","Hiragino Sans",sans-serif`
    // 右上：窓の長さと間（いつも）
    ctx.fillStyle = '#e8c27a'; ctx.font = font(12); ctx.textAlign = 'right'
    ctx.fillText(`窓 ${(s.seg.win / 1000).toFixed(1)}秒 ・ 間 ${(s.seg.gap / 1000).toFixed(1)}秒`, w - 10 * k, 20 * k)
    ctx.fillStyle = '#7c8492'; ctx.font = font(9)
    ctx.fillText(`${s.i + 1} / ${T.length}`, w - 10 * k, 34 * k)
    // 床の下の帯：窓（黄）と間（赤）。段の位置は描かない（数を帯で教えない）
    const bx = 30 * k, bw = w - 60 * k, by = G + 22 * k, bh = 6 * k
    const total = s.seg.win + s.seg.gap, X = (ms: number) => bx + ms / total * bw
    ctx.fillStyle = '#2a303b'; ctx.fillRect(bx, by, bw, bh)
    const pw = clamp01((t - s.ws) / s.seg.win), pg = clamp01((t - s.we) / Math.max(1, s.seg.gap))
    ctx.fillStyle = 'rgba(232,194,122,.85)'; ctx.fillRect(bx, by, X(s.seg.win) - bx, bh * 0.35); ctx.fillRect(bx, by, (X(s.seg.win) - bx) * pw, bh)
    ctx.fillStyle = 'rgba(239,91,99,.85)'; ctx.fillRect(X(s.seg.win), by, (X(total) - X(s.seg.win)), bh * 0.35); ctx.fillRect(X(s.seg.win), by, (X(total) - X(s.seg.win)) * pg, bh)
    ctx.font = font(9); ctx.textAlign = 'left'; ctx.fillStyle = '#9a9cab'
    ctx.fillText('予告の窓', bx, by + 18 * k)
    ctx.textAlign = 'right'; ctx.fillText('間 → 技', X(total), by + 18 * k)
    // 答え：窓が終わってから出す（見ながら数えてもらうため）
    if (t >= s.we) {
      ctx.globalAlpha = smooth(ramp(t, s.we, s.we + 200))
      ctx.fillStyle = '#ffffff'; ctx.font = font(15); ctx.textAlign = 'center'
      ctx.fillText(`答え：${s.seg.n}回（${BOSS_OF[s.seg.n] ?? ''}）`, w / 2, 56 * k)
      ctx.globalAlpha = 1
    }
    // 大技が当たった瞬間の赤
    const d = t - s.L
    if (d >= 0 && d < 220) { ctx.globalAlpha = 0.2 * (1 - d / 220); ctx.fillStyle = '#ef5b63'; ctx.fillRect(0, 0, e.w, e.h); ctx.globalAlpha = 1 }
  }
}
const BOSS_OF: Record<number, string> = { 1: '戴冠獣', 2: '騎士', 3: '花嫁', 4: '介錯', 5: '覚醒獣' }

// ---------- 先代の体：窓の間は低く構えて溜め、間の終わりに踏み込んで突く（技の中身は仮）----------
const PREP = { px: -4, py: -66, lean: 0, tw: -24, aF: 120, bF: 6, sF: 0 }
const LUNGE = { px: 40, py: -54, lean: 26, tw: 30, aF: -6, bF: -10, sF: -2 }
type K = keyof typeof PREP
function ringTracks(segs: RingSeg[]): Tracks {
  const T = segTimes(segs)
  const out: Record<string, TrackKey[]> = {}
  for (const key of Object.keys(PREP) as K[]) out[key] = [[0, STAND[key] as number]]
  out.fF = [[0, [12, 0]]]; out.fB = [[0, [-10, 0]]]
  out.aB = [[0, 120]]; out.bB = [[0, 68]]
  for (const { ws, L } of T) {
    for (const key of Object.keys(PREP) as K[]) {
      out[key]!.push([ws - RB.travel, STAND[key] as number, 'io'], [ws + 400, PREP[key], 'io'], [L - 80, PREP[key] + (key === 'py' ? 2 : 0)],
        [L, LUNGE[key], 'in'], [L + 400, LUNGE[key]], [L + 800, STAND[key] as number, 'io'])
    }
    out.fF!.push(...step(L - 120, L, 12, 64, 22), ...step(L + 520, L + 720, 64, 24, 10), ...step(L + 860, L + 950, 24, 12, 8))
    out.fB!.push(...step(L + 400, L + 520, -10, 4, 9), ...step(L + 950, L + 1060, 4, -10, 7))
    // 奥の手は腰のまま、踏み込みで後ろへ流す
    out.aB!.push([L - 60, 120], [L + 80, 150, 'io'], [L + 400, 150], [L + 800, 120, 'io'])
    out.bB!.push([L - 60, 68], [L + 80, 140, 'io'], [L + 400, 140], [L + 800, 68, 'io'])
  }
  return out
}

function ringPhases(segs: RingSeg[]): MockupPhase[] {
  return segTimes(segs).flatMap(({ o, ws, we, L }) => [
    { from: o, to: ws - RB.travel, label: '構え（輪は先代を照らす）', kind: 'rest' as const },
    { from: ws - RB.travel, to: ws, label: '輪が主人公へ移る', kind: 'rest' as const },
    { from: ws, to: we, label: '予告の窓：輪が狭まる回数を数える', kind: 'tell' as const },
    { from: we, to: L, label: '窓の終わり → 間', kind: 'tell' as const },
    { from: L, to: L + 400, label: '技（仮の踏み込み突き）：白い輪へ', kind: 'hit' as const },
    { from: L + 400, to: L + RB.after, label: '戻り（輪は先代へ戻る）', kind: 'rest' as const },
  ])
}

function ringMove(id: string, label: string, segs: RingSeg[]): MockupMove {
  const T = segTimes(segs), dur = T.reduce((a, r) => a + segLen(r.seg), 0)
  return {
    id, boss: '先代の姫', label, body: 'human-rapier-skirt', durMs: dur, scale: 0.95, baseX: 300, heroAt: 150, interp: 'linear',
    keys: bakeTracks(ringTracks(segs), dur, build), phases: ringPhases(segs), hits: T.map(r => r.L), activeMs: 60,
    box: [40, 120, -128, -88], paths: ['sF'],
    cues: T.flatMap(r => stepTimes(r.ws, r.seg)).map(at => ({ at, ms: 60 })),
    source: `数え方の試し（11-05 の決まり）。予告の窓の中で輪が N 段に等間隔に狭まり（k 段目は窓の (k−0.5)/N）、最後の段で主人公の位置に止まって白くなる。窓の後に「間」を置いて技。窓と間の数字は字幕。並び：${segs.map(s => `${s.n}回`).join('→')}。`,
    stage: { back: drawRing(segs), front: drawCaption(segs) },
    hero: (ctx, t, e) => drawHeroFig(ctx, heroAt(T, t), e),
  }
}

/** 数えてもらう並び（順番を混ぜる）。窓の長さ3つ・間の長さ3つ（どれも仮） */
export const RING_ORDER = [3, 5, 1]
export const RING_WINS = [1500, 2000, 2500]
export const RING_GAPS = [300, 500, 800]
const BASE_GAP = 500, BASE_WIN = 2000
export const PRINCESS_RING_WIN_MOCKUPS = RING_WINS.map(win => ringMove(`princess-ring-win${win}`,
  `指折りの輪 窓 ${(win / 1000).toFixed(1)}秒（3回→5回→1回・間 ${BASE_GAP / 1000}秒）`, RING_ORDER.map(n => ({ n, win, gap: BASE_GAP }))))
export const PRINCESS_RING_GAP_MOCKUP = ringMove('princess-ring-gap', `指折りの輪 間の長さ ${RING_GAPS.map(g => (g / 1000).toFixed(1)).join('／')}秒（3回・窓 2秒）`,
  RING_GAPS.map(gap => ({ n: 3, win: BASE_WIN, gap })))

// ---------- 「えっと……」の4つの構え（輪で数えない・構えの形で見分ける）----------
//   どれも仮の形。借り元のボスの技の始まりを、影の形の違いが出る所だけ似せた。
//   亡霊＝低く前へかがみ、剣を後ろに垂らす／鶏＝胸を張って反り、両腕を羽のように後ろへ・つま先立ち／
//   軍配者たち＝剣を顔の前に立て（軍配のように）、隣に声の灯が灯る／剣舞＝両腕を左右へ大きく広げ、片足を上げる。
type Stance = { name: string; hint: string; pose: Partial<Record<string, number | Vec>>; ember?: boolean }
export const ETTO_STANCES: Stance[] = [
  { name: '亡霊', hint: '低くかがみ、剣を後ろへ垂らす', pose: { px: 4, py: -56, lean: 34, tw: -6, aF: 110, bF: 130, sF: 160, aB: 96, bB: 100, fF: [20, 0], fB: [-20, 0] } },
  { name: '鶏', hint: '胸を張って反り、両腕を羽のように後ろへ', pose: { px: 0, py: -76, lean: -16, tw: 10, aF: 165, bF: 200, sF: 215, aB: 175, bB: 210, fF: [8, 0], fB: [-6, 0], rF: 0.9, rB: 0.9 } },
  { name: '軍配者たち', hint: '剣を顔の前に立て、隣に声の灯', pose: { px: 0, py: -70, lean: 2, tw: 6, aF: 30, bF: -95, sF: -90, aB: 120, bB: 68, fF: [10, 0], fB: [-12, 0] }, ember: true },
  { name: '剣舞', hint: '両腕を左右へ広げ、片足を上げる', pose: { px: 0, py: -74, lean: 4, tw: 40, aF: -12, bF: -10, sF: -14, aB: 192, bB: 188, fF: [6, 0], fB: [-26, -34], rF: 1 } },
]
const ST = { stand: 300, rise: 450, hold: 1000, down: 350 }
export const ETTO_LEN = ST.stand + ST.rise + ST.hold + ST.down
/** k 番目の構えを保っている時刻（止め絵を切り出す所） */
export const ettoHoldAt = (i: number) => i * ETTO_LEN + ST.stand + ST.rise + ST.hold * 0.6
function ettoTracks(): Tracks {
  const keys = new Set<string>(['px', 'py', 'lean', 'tw', 'aF', 'bF', 'sF', 'aB', 'bB', 'fF', 'fB', 'rF', 'rB'])
  const base: Record<string, number | Vec> = { ...STAND, rF: 0, rB: 0 }
  const out: Tracks = {}
  for (const k of keys) out[k] = [[0, base[k]!]]
  ETTO_STANCES.forEach((s, i) => {
    const o = i * ETTO_LEN
    for (const k of keys) {
      const v = s.pose[k] ?? base[k]!
      // 片足を上げる構えは、上げる足を最後に動かす（両足が同時に浮かない）
      out[k]!.push([o + ST.stand, base[k]!], [o + ST.stand + ST.rise, v, 'io'], [o + ST.stand + ST.rise + ST.hold, v], [o + ETTO_LEN, base[k]!, 'io'])
    }
  })
  return out
}
const ETTO_TR = ettoTracks()
function drawEttoCaption(ctx: Ctx, t: number, e: StageEnv): void {
  const { k, G, w } = e, i = Math.min(ETTO_STANCES.length - 1, Math.floor(t / ETTO_LEN)), s = ETTO_STANCES[i]!
  const u = t - i * ETTO_LEN, on = smooth(ramp(u, ST.stand, ST.stand + ST.rise)) * (1 - smooth(ramp(u, ETTO_LEN - ST.down, ETTO_LEN)))
  ctx.globalAlpha = 0.35 + 0.65 * on; ctx.fillStyle = '#ffffff'; ctx.textAlign = 'center'
  ctx.font = `${15 * k}px "Zen Kaku Gothic New","Hiragino Sans",sans-serif`; ctx.fillText(`${i + 1}. ${s.name}`, w / 2, G + 34 * k)
  ctx.fillStyle = '#9a9cab'; ctx.font = `${10 * k}px "Zen Kaku Gothic New","Hiragino Sans",sans-serif`; ctx.fillText(s.hint, w / 2, G + 52 * k)
  ctx.globalAlpha = 1
  if (s.ember && on > 0.01) {
    // 声の灯（一粒の灯の仮の形：白い芯に青のにじみ）。先代の前、肩の高さ
    const p = e.at([34, -150 - Math.sin(t / 260) * 3])
    ctx.globalAlpha = 0.35 * on; circ(ctx, p, 9 * k, '#7fc4ff'); ctx.globalAlpha = on; circ(ctx, p, 3 * k, '#ffffff'); ctx.globalAlpha = 1
  }
}
export const PRINCESS_ETTO_STANCES_MOCKUP: MockupMove = {
  id: 'princess-etto-stances', boss: '先代の姫', label: '「えっと……」の4つの構え（亡霊・鶏・軍配者たち・剣舞・形は仮）', body: 'human-rapier-skirt',
  durMs: ETTO_LEN * ETTO_STANCES.length, scale: 0.95, baseX: 260, heroAt: 150, interp: 'linear',
  keys: bakeTracks(ETTO_TR, ETTO_LEN * ETTO_STANCES.length, build),
  phases: ETTO_STANCES.map((s, i) => ({ from: i * ETTO_LEN, to: (i + 1) * ETTO_LEN, label: `「えっと……」の構え ${i + 1}：${s.name}`, kind: 'tell' as const })),
  hits: [], activeMs: 0, box: [40, 120, -128, -88], paths: ['sF'],
  source: '輪では数えない4体（亡霊・鶏・軍配者たち・剣舞）を、技の始めの構えの影の形で見分けられるかの見本。形は仮。',
  stage: { front: drawEttoCaption },
}
/** 止め絵用：k 番目の構えの姿勢 */
export function ettoPose(i: number): Pose {
  const keys = PRINCESS_ETTO_STANCES_MOCKUP.keys, at = ettoHoldAt(i)
  let j = 0
  while (j < keys.length - 1 && keys[j + 1]!.at <= at) j++
  return keys[j]!.pose
}

export const PRINCESS_RING_MOCKUPS = [...PRINCESS_RING_WIN_MOCKUPS, PRINCESS_RING_GAP_MOCKUP, PRINCESS_ETTO_STANCES_MOCKUP]
